import React from 'react';
import { capitalize } from 'lodash';
import { getSkillLevelLabel, normalizeSkillLevel } from '@/domains';
import { Tag, TagProps } from '@/components';
import { TScoreSkillLevel } from '@/types';

export interface SkillLevelTagProps extends TagProps {
  /**
   * The skill level to be displayed in the tag. This determines the label and styling of the tag.
   *
   *  - Accepts canonical skill levels and recognized legacy aliases.
   *  - Legacy aliases are normalized to the canonical skill level.
   *  - If no skill level is provided, the component will not render anything.
   */
  skillLevel?: TScoreSkillLevel | 'novice';
}

/**
 * SkillLevelTag
 *
 * A reusable component that displays a tag indicating the skill level of a user based on their score.
 * - Canonical level 1 variant is styled with yellow/orange colors.
 * - 'intermediate' variant is styled with orange colors.
 * - 'advanced' variant is styled with blue colors.
 * - 'expert' variant is styled with green colors.
 *
 * The component does not render anything if no score is provided.
 */
export const SkillLevelTag: React.FC<SkillLevelTagProps> = ({
  className,
  skillLevel,
  ...props
}) => {
  // If no skill level provided, we don't render a tag.
  if (!skillLevel || skillLevel === null) {
    return null;
  }

  const normalizedSkillLevel = normalizeSkillLevel(skillLevel);
  if (!normalizedSkillLevel) {
    return null;
  }

  /**
   * Convert the normalized skill level to a key for the SKILL_LEVEL_LABELS object.
   * This ensures that we can safely access the corresponding label for the skill level.
   */

  /**
   * Get the display label for the normalized skill level
   * Handles both canonical codes and legacy aliases
   */
  const skillLevelLabel = getSkillLevelLabel(normalizedSkillLevel);

  // ================================
  // Render Logic
  // ================================
  // Render the Tag component with the appropriate label
  // and variant based on the skill level.
  return (
    <Tag className={className} label={skillLevelLabel} variant={normalizedSkillLevel} {...props} />
  );
};

SkillLevelTag.displayName = 'SkillLevelTag';

export default SkillLevelTag;
